import React, { useEffect, useRef, useState, useMemo } from 'react';
import {
  collection,
  getDocs,
  doc,
  getDoc,
  runTransaction,
  addDoc,
  serverTimestamp,
  updateDoc,
  deleteDoc,
  setDoc,
  getDoc as fetchDoc,
} from 'firebase/firestore';
import toast, { Toaster } from 'react-hot-toast';
import { db } from '../protected/firebase';
import { useAuth } from '../../../contexts/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import QRCode from 'react-qr-code';
import NoImage from '../../../assets/images/no-image.jpg';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';

const ANY_VARIANT_ID = '__ANY_VARIANT__';

export default function CashierPage() {
  const { user, companyId } = useAuth();
const [products, setProducts] = useState([]);
const [cart, setCart] = useState([]);
  const [search, setSearch] = useState('');
  const [discountOptions, setDiscountOptions] = useState([]);
  const [selectedDiscount, setSelectedDiscount] = useState(null);
  const [discountCodeInput, setDiscountCodeInput] = useState('');
  const [discountError, setDiscountError] = useState('');
  const [appliedCode, setAppliedCode] = useState('');
  const [mode, setMode] = useState('');
  const [receiptNumber, setReceiptNumber] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('Cash');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [cashInput, setCashInput] = useState('');
  const [showReceipt, setShowReceipt] = useState(false);
  const [companyInfo, setCompanyInfo] = useState({});
  const [companySnap, setCompanySnap] = useState({});
  const [finalReceipt, setFinalReceipt] = useState(null);
  const [showOrder, setShowOrder] = useState(false);
  const navigate = useNavigate();
  const receiptRef = useRef();
const [stallType, setStallType] = useState('');
const [cashiers, setCashiers] = useState([]);
const [selectedCashier, setSelectedCashier] = useState('');
const savedOrderRef = useRef({});
  const isDiscountWithinValidity = (discount) => {
    if (!discount) return true;
    const normalizeDate = (value, endOfDay = false) => {
      if (!value) return null;
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return null;
      if (endOfDay) {
        d.setHours(23, 59, 59, 999);
      } else {
        d.setHours(0, 0, 0, 0);
      }
      return d;
    };
    const now = new Date();
    const start = normalizeDate(discount.validityStart, false);
    const end = normalizeDate(discount.validityEnd, true);
    if (start && now < start) return false;
    if (end && now > end) return false;
    return true;
  };
const [customerName, setCustomerName] = useState('');
const [qrModalOpen, setQrModalOpen] = useState(false);
const [qrUrl, setQrUrl] = useState('');
const [cardRefNum, setCardRefNum] = useState('');
const [sst6Label, setSst6Label] = useState('');
const [sst8Label, setSst8Label] = useState('');
const [serviceTax10Label, setServiceTax10Label] = useState('');
const [sst6Enabled, setSst6Enabled] = useState(false);
const [sst8Enabled, setSst8Enabled] = useState(false);
const [serviceTax10Enabled, setServiceTax10Enabled] = useState(false);
const [sst6Rate, setSst6Rate] = useState(0);
const [sst8Rate, setSst8Rate] = useState(0);
const [serviceTax10Rate, setServiceTax10Rate] = useState(0);
const [taxEnabled, setTaxEnabled] = useState(false);
const [bundles, setBundles] = useState([]);
const [publicId, setPublicId] = useState('');
const [itemNotes, setItemNotes] = useState({});
const [onlineOrders, setOnlineOrders] = useState([]);
const [selectedOnlineOrderId, setSelectedOnlineOrderId] = useState('');
const [selectedOnlineOrderData, setSelectedOnlineOrderData] = useState(null);

const bc = new BroadcastChannel("pos_display");

  useEffect(() => {
    if (serviceTax10Enabled && Number(serviceTax10Rate) > 0) {
      setServiceTax10Label(`Service Tax (${Number(serviceTax10Rate)}%)`);
    } else if (serviceTax10Enabled) {
      setServiceTax10Label('Service Tax');
    } else {
      setServiceTax10Label('');
    }
  }, [serviceTax10Enabled, serviceTax10Rate]);

  const location = useLocation();
  const selectedType = location.state?.selectedType || null;

  const filteredProducts = useMemo(() => {
    let itemsToShow = [];
    
    if (selectedType === 'bundle') {
      itemsToShow = [...bundles];
    } else if (selectedType) {
      itemsToShow = products.filter(product => product.type === selectedType);
    } else {
      itemsToShow = [...products, ...bundles];
    }
    
    return itemsToShow.filter(item => {
      return item.name.toLowerCase().includes(search.toLowerCase()) ||
             item.sku?.toLowerCase().includes(search.toLowerCase()) ||
             item.barcode?.toLowerCase().includes(search.toLowerCase());
    });
  }, [products, bundles, search, selectedType]);

  const getTodayDate = () => new Date().toISOString().split('T')[0];

  const formatDate = (date) => {
    const d = new Date(date);
    return `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1)
      .toString()
      .padStart(2, '0')}/${d.getFullYear()}`;
  };

  const formatTime = (date) => {
    const d = new Date(date);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  useEffect(() => {
    const ensureSessionStarted = async () => {
      if (!companyId) return;
      try {
        const sessionRef = doc(db, 'companies', companyId, 'cashSessions', getTodayDate());
        const snap = await getDoc(sessionRef);
        if (!snap.exists()) {
          toast.error('Please perform Start of Day before making transactions.');
          navigate('/product-view', { replace: true });
          return;
        }
        const data = snap.data() || {};
        if (data.endedAt) {
          toast.error('Day is closed. Start a new day to continue sales.');
          navigate('/product-view', { replace: true });
        }
      } catch (err) {
        console.error('Failed to check start of day:', err);
      }
    };
    ensureSessionStarted();
  }, [companyId, navigate]);

  // Placeholder: previously refreshed queue display; keep to avoid runtime errors
  const loadQueueOrders = async () => {};

  const loadOnlineOrders = async () => {
    if (!companyId) return;
    try {
      const ref = collection(db, 'companies', companyId, 'preorders');
      const snap = await getDocs(ref);
      const list = [];
      snap.forEach((d) => {
        const data = d.data();
        const isOnline = (data.orderMode || data.mode || '').toString().toUpperCase().includes('ONLINE') || (data.mode || '').toString().toUpperCase().includes('PICK');
        const unpaid = (data.paymentStatus || '').toLowerCase() !== 'paid';
        const status = (data.orderStatus || '').toLowerCase();
        const isCompleted = status === 'completed' || status === 'complete';
        // Include all unpaid online orders (both completed and not completed)
        if (isOnline && unpaid) {
          list.push({ id: d.id, ...data, isCompleted });
        }
      });
      list.sort((a, b) => {
        const ta = a.timestamp?.toMillis ? a.timestamp.toMillis() : new Date(a.createdAt || a.timestamp || 0).getTime();
        const tb = b.timestamp?.toMillis ? b.timestamp.toMillis() : new Date(b.createdAt || b.timestamp || 0).getTime();
        return tb - ta;
      });
      setOnlineOrders(list);
    } catch (err) {
      console.error('Failed to load online orders:', err);
      toast.error('Unable to fetch online orders');
    }
  };



  const getNextCounter = async (companyId, counterName) => {
    const counterRef = doc(db, 'companies', companyId, 'companySettings', counterName);
    return await runTransaction(db, async (transaction) => {
      const docSnap = await transaction.get(counterRef);
      const today = new Date().toISOString().split('T')[0];
      let current = 0;
      let initialDate = today;
  
      if (docSnap.exists()) {
        const data = docSnap.data();
        const lastUpdatedDate = data?.lastUpdated?.toDate?.().toISOString().split('T')[0];
        if (lastUpdatedDate === today) {
          current = data.current || 0;
        } else {
          current = 0;
        }
      }
  
      const next = current + 1;
      transaction.set(counterRef, {
        current: next,
        lastUpdated: new Date(),
        updatedAt: serverTimestamp(),
        initialDate: today
      });
      return next;
    });
  };


  const handleNote = (itemId, note) => {
    // Update the note in the cart
    setCart(prev => 
      prev.map(item => 
        item.id === itemId 
          ? { ...item, note } 
          : item
      )
    );
    
    // Update the itemNotes state
    setItemNotes(prev => ({
      ...prev,
      [itemId]: note
    }));
  };

  const populateCartFromOnlineOrder = (order) => {
    if (!order) return;
    const mappedCart = (order.items || []).map((it) => {
      const productRef = products.find((p) => p.id === it.id);
      const variantObj = productRef?.variants?.find(
        (v) => (v.id || v.variantId) === (it.variantId || it.variantid)
      );
      const variantDelta =
        Number(it.variantPriceDelta || 0) ||
        (variantObj ? computeVariantDelta(productRef, variantObj) : 0);

      const bundleItems =
        it.type === 'bundle' && Array.isArray(it.items)
          ? it.items.map((sub) => {
              const subProduct = products.find((p) => p.id === sub.id);
              const subVariant = subProduct?.variants?.find(
                (v) => (v.id || v.variantId) === sub.variantId
              );
              const subDelta =
                Number(sub.variantPriceDelta || 0) ||
                (subVariant ? computeVariantDelta(subProduct, subVariant) : 0);
              return {
                ...sub,
                variantPriceDelta: subDelta,
                selectedOptions: sub.selectedOptions || sub.options || subVariant?.options || [],
              };
            })
          : undefined;

      return {
        id: it.id,
        name: it.name,
        price: Number(it.price || 0),
        qty: it.qty ?? it.quantity ?? 1,
        quantity: it.qty ?? it.quantity ?? 1,
        note: it.note || '',
        variantId: it.variantId || null,
        variantName: it.variantName || (variantObj ? getVariantLabel(variantObj) : null),
        variantPriceDelta: variantDelta,
        selectedOptions: it.selectedOptions || it.options || variantObj?.options || [],
        type: it.type || 'product',
        items: bundleItems,
      };
    });

    const notes = {};
    mappedCart.forEach((it) => {
      if (it.note) {
        notes[it.id] = it.note;
      }
    });

    // Save original quantities so we don't re-deduct stock for online orders
    const savedMap = mappedCart.reduce((acc, it) => {
      acc[it.id] = (acc[it.id] || 0) + (it.qty || it.quantity || 0);
      return acc;
    }, {});
    savedOrderRef.current = savedMap;

    setCart(mappedCart);
    setItemNotes(notes);
    sessionStorage.setItem('cashierCart', JSON.stringify(mappedCart));
    sessionStorage.setItem('cashierNotes', JSON.stringify(notes));
    setMode('Online');
  };

  const handleSelectOnlineOrder = (orderId) => {
    // Clear existing cart when switching to an online order
    setCart([]);
    setItemNotes({});
    sessionStorage.removeItem('cashierCart');
    sessionStorage.removeItem('cashierNotes');

    setSelectedOnlineOrderId(orderId);
    const order = onlineOrders.find((o) => o.id === orderId);
    setSelectedOnlineOrderData(order || null);
    if (order) {
      populateCartFromOnlineOrder(order);
    }
  };

  const handleClearAll = () => {
    if (cart.length === 0) return;
    
    if (window.confirm('Are you sure you want to clear all items from the cart?')) {
      setCart([]);
      setItemNotes({});
      setSelectedDiscount(null);
      setAppliedCode('');
      setDiscountError('');
      setDiscountCodeInput('');
      sessionStorage.removeItem('cashierCart');
      sessionStorage.removeItem('cashierNotes');
      toast.success('Cart cleared successfully');
    }
  };

  const addVariantToCart = (product, variantId) => {
    const variant = product.variants?.find((v) => v.id === variantId || v.variantId === variantId);
    if (!variant) {
      toast.error('Variant not found');
      return;
    }
    if (variant.qty <= 0) {
      toast.error('Variant out of stock');
      return;
    }
    if (wouldExceedProductAdd(product.id, variant.id, 1)) {
      toast.error('Maximum available quantity reached for this variant');
      return;
    }
    setCart((prev) => {
      const existing = prev.find(
        (c) => c.id === product.id && c.variantId === variant.id
      );
      const variantDelta = computeVariantDelta(product, variant);
      const newQty = existing ? existing.qty + 1 : 1;
      if (newQty > variant.qty) {
        toast.error('Maximum available quantity reached for this variant');
        return prev;
      }
      const updated = existing
        ? prev.map((c) =>
            c.id === product.id && c.variantId === variant.id
              ? {
                  ...c,
                  qty: newQty,
                  price: computeUnitPrice(product, variant),
                  variantPriceDelta: variantDelta,
                  selectedOptions: variant?.options || [],
                }
              : c
          )
        : [
            ...prev,
            {
              ...product,
              variantId: variant.id,
              variantName: (variant.options || [])
                .map((o) => `${o.group}: ${o.value}`)
                .join(' / '),
              price: computeUnitPrice(product, variant),
              qty: 1,
              variantPriceDelta: variantDelta,
              selectedOptions: variant?.options || [],
            },
          ];
      return updated;
    });
  };

  const handleAddToCart = (item) => {
    // Check if the item is a bundle
    const isBundle = bundles.some(b => b.id === item.id);
    const currentNote = itemNotes[item.id] || '';
  
    // For bundles, support "choose variant at checkout"
    if (isBundle) {
      const needsVariantChoice = (item.items || []).some((bundleItem) =>
        isAnyVariantItem(bundleItem)
      );

      if (needsVariantChoice) {
        const selections = {};
        (item.items || []).forEach((bundleItem) => {
          if (!isAnyVariantItem(bundleItem)) return;
          const product = products.find((p) => p.id === bundleItem.id);
          const def = defaultVariantForProduct(product);
          const count = Math.max(1, Number(bundleItem.quantity) || 1);
          selections[bundleItem.id] = Array(count).fill(def?.id || '');
        });
        setBundleVariantContext({ bundle: item, selections, note: currentNote });
        setBundleVariantModalOpen(true);
        return;
      }

      const { resolvedItems, deltaTotal, configKey } = buildBundleConfig(item, {});
      const candidateBundle = { ...item, items: resolvedItems };

      if (wouldExceedBundleAdd(candidateBundle, 1)) {
        toast.error('Adding this bundle would exceed available stock for some items');
        return;
      }

      const outOfStockItem = resolvedItems.find((bundleItem) => {
        const product = products.find((p) => p.id === bundleItem.id);
        if (!product) return true;
        const availableStock = getAvailableStock(
          bundleItem.id,
          bundleItem.variantId || null
        );
        const reserved = getReservedStock(
          bundleItem.id,
          bundleItem.variantId || null,
          cart
        );
        return reserved + (bundleItem.quantity || 1) > availableStock;
      });
  
      if (outOfStockItem) {
        toast.error(`Not enough stock for ${outOfStockItem.name} in bundle`);
        return;
      }

      addBundleLineToCart(item, resolvedItems, deltaTotal, configKey, currentNote);
      return;
    }

    // For regular products, check stock
    if (item.qty <= 0) {
      toast.error('Item out of stock');
      return;
    } else if (item.hasVariants && item.variants?.length) {
      setVariantModalProduct(item);
      setVariantModalVariantId('');
      setVariantModalOpen(true);
      return;
    }
    // For simple products, ensure we won't exceed stock considering other cart lines/bundles
    if (!isBundle && !item.hasVariants) {
      if (wouldExceedProductAdd(item.id, item.variantId || null, 1)) {
        toast.error('Maximum available quantity reached');
        return;
      }
    }

    setCart((prev) => {
      const existingItem = prev.find(cartItem => cartItem.id === item.id);
      
      // For regular products, check stock
      if (!isBundle && wouldExceedProductAdd(item.id, item.variantId || null, 1)) {
        toast.error('Maximum available quantity reached');
        return prev;
      }
      
      // Update the cart
      const newCart = existingItem
        ? prev.map(cartItem => 
            cartItem.id === item.id 
              ? { ...cartItem, 
                qty: cartItem.qty + 1,
                note: cartItem.note || currentNote
              }
              : cartItem
          )
        : [...prev, { ...item, 
          qty: 1,
          note: currentNote
        }];
      
      return newCart;
    });
  };

  const handleQtyChange = (id, change, variantId = null, lineIndex = null) => {
    setCart((prev) => {
      const targetIdx =
        lineIndex !== null
          ? lineIndex
          : prev.findIndex(
              (entry) => entry.id === id && (variantId ? entry.variantId === variantId : true)
            );
      if (targetIdx === -1) return prev;

      const item = prev[targetIdx];
      const newQty = (item.qty || 0) + change;
      const targetVariant = item.variantId || null;

      if (newQty <= 0) {
        setItemNotes((prevNotes) => {
          const newNotes = { ...prevNotes };
          delete newNotes[id];
          return newNotes;
        });
        const filtered = prev.filter((_, idx) => idx !== targetIdx);
        sessionStorage.setItem("cashierCart", JSON.stringify(filtered));
        return filtered;
      }

      const isBundleLine = bundles.some((b) => b.id === id) || item.items;
      if (change > 0) {
        if (isBundleLine) {
          const bundleData = bundles.find((b) => b.id === id) || item;
          if (wouldExceedBundleAdd(bundleData, change)) {
            toast.error('Adding this bundle would exceed available stock for some items');
            return prev;
          }
        } else if (wouldExceedProductAdd(id, targetVariant, change)) {
          toast.error(targetVariant ? 'Maximum available quantity reached for this variant' : 'Maximum available quantity reached');
          return prev;
        }
      }

      const updatedCart = prev.map((entry, idx) =>
        idx === targetIdx ? { ...entry, qty: newQty, note: entry.note } : entry
      );
      sessionStorage.setItem("cashierCart", JSON.stringify(updatedCart));
      return updatedCart;
    });
  };

  const subtotal = cart.reduce((sum, i) => sum + i.price * i.qty, 0);
  let sst6Amount = 0;
  if (sst6Enabled) {
    sst6Amount = subtotal * (sst6Rate / 100);
  }
  let sst8Amount = 0;
  if (sst8Enabled) {
    sst8Amount = subtotal * (sst8Rate / 100);
  }
  let serviceTax10Amount = 0;
  if (serviceTax10Enabled) {
    serviceTax10Amount = subtotal * (serviceTax10Rate / 100);
  }
  const taxAmount = sst6Amount + sst8Amount + serviceTax10Amount;
  const discountBase = subtotal + taxAmount;
  const rawDiscount = selectedDiscount
    ? selectedDiscount.value.toString().includes('%')
      ? discountBase * (parseFloat(selectedDiscount.value) / 100)
      : parseFloat(selectedDiscount.value)
    : 0;
  // Guard against over-discounting so totals remain non-negative
  const discountAmount = Math.max(0, Math.min(rawDiscount, discountBase));

  const roundToNearestFiveCents = (amount) => Math.round(amount * 20) / 20;
  const rawTotal = Math.max(0, subtotal + taxAmount - discountAmount);
  const roundedTotal = paymentMethod === 'Cash' ? roundToNearestFiveCents(rawTotal) : Math.round(rawTotal * 100) / 100;
  const rounding = roundedTotal - rawTotal;
  const total = roundedTotal;
  const effectiveReceived = paymentMethod === 'Cash' ? parseFloat(cashInput || 0) : total;
  const balance = effectiveReceived - total;
  const [confirmLoading, setConfirmLoading] = useState(false);
  const isAnyVariantItem = (bundleItem) => bundleItem?.variantId === ANY_VARIANT_ID || bundleItem?.allowVariantChoice;
  const getTotalVariantQty = (product) =>
    Array.isArray(product?.variants)
      ? product.variants.reduce((sum, v) => sum + Number(v.qty || 0), 0)
      : 0;

  const calculateBundleQuantity = (bundle, productsList) => {
    if (!bundle.items || bundle.items.length === 0) return 0;
    let minQty = Infinity;
    for (const item of bundle.items) {
      const product = productsList.find((p) => p.id === item.id);
      if (!product) return 0;
      const totalVariantQty = getTotalVariantQty(product);
      let availableStock = typeof product.qty === 'number' ? product.qty : totalVariantQty;
      if (isAnyVariantItem(item)) {
        availableStock = availableStock || totalVariantQty;
      } else if (item.variantId && product.variants?.length) {
        const variant = product.variants.find(
          (v) => v.id === item.variantId || v.variantId === item.variantId
        );
        availableStock = variant?.qty || 0;
      }
      const perItemRequired = item.quantity || 1;
      const possibleSets = Math.floor(availableStock / perItemRequired);
      minQty = Math.min(minQty, possibleSets);
    }
    return Number.isFinite(minQty) ? Math.max(0, minQty) : 0;
  };

  const getAvailableStock = (productId, variantId = null) => {
    const product = products.find((p) => p.id === productId);
    if (!product) return 0;
    if (variantId === ANY_VARIANT_ID) {
      const totalVariantQty = getTotalVariantQty(product);
      return typeof product.qty === 'number' ? product.qty : totalVariantQty;
    }
    if (variantId && product.variants?.length) {
      const variant = product.variants.find(
        (v) => v.id === variantId || v.variantId === variantId
      );
      return variant?.qty || 0;
    }
    return product.qty || 0;
  };

  const getReservedStock = (productId, variantId = null, cartItems = []) => {
    let reserved = 0;
    for (const line of cartItems) {
      const isBundleLine = bundles.some((b) => b.id === line.id) || line.items;
      if (isBundleLine) {
        for (const bItem of line.items || []) {
          const matchesProduct = bItem.id === productId;
          const matchesVariant =
            variantId === ANY_VARIANT_ID
              ? true
              : (variantId || null) === (bItem.variantId || null) ||
                (!variantId && !bItem.variantId);
          if (matchesProduct && matchesVariant) {
            reserved += (bItem.quantity || 1) * (line.qty || 1);
          }
        }
      } else {
        const matchesProduct = line.id === productId;
        const matchesVariant =
          variantId === ANY_VARIANT_ID
            ? true
            : (variantId || null) === (line.variantId || null) ||
              (!variantId && !line.variantId);
        if (matchesProduct && matchesVariant) {
          reserved += line.qty || 0;
        }
      }
    }
    return reserved;
  };

  const wouldExceedBundleAdd = (bundle, addQty = 1) => {
    for (const item of bundle.items || []) {
      const available = getAvailableStock(item.id, item.variantId || null);
      const reserved = getReservedStock(item.id, item.variantId || null, cart);
      const required = (item.quantity || 1) * addQty;
      if (reserved + required > available) {
        return true;
      }
    }
    return false;
  };

  const wouldExceedProductAdd = (productId, variantId = null, addQty = 1) => {
    const available = getAvailableStock(productId, variantId);
    const reserved = getReservedStock(productId, variantId, cart);
    return reserved + addQty > available;
  };

  const getVariantLabel = (variant) =>
    (variant?.options || []).map((o) => `${o.group}: ${o.value}`).join(' / ');

  const getValuePrice = (product, groupName, valueLabel) => {
    if (!product?.options) return 0;
    const norm = (v) => (v || '').toString().trim().toLowerCase();
    const group = product.options.find((g) => norm(g.name) === norm(groupName));
    if (!group || !Array.isArray(group.values)) return 0;
    const match = group.values.find(
      (v) => norm(v.label ?? v.value ?? v) === norm(valueLabel)
    );
    return Number(match?.price || 0);
  };

  const computeVariantDelta = (product, variant) => {
    if (!product || !variant) return 0;
    return (variant.options || []).reduce(
      (sum, opt) => sum + getValuePrice(product, opt.group, opt.value),
      0
    );
  };

  const computeUnitPrice = (product, variant) => {
    const base = Number(product?.price || 0);
    const delta = computeVariantDelta(product, variant);
    return base + delta;
  };

  const defaultVariantForProduct = (product) => {
    if (!product?.variants?.length) return null;
    return product.variants.find((v) => (v.qty || 0) > 0) || product.variants[0];
  };

  const buildBundleConfig = (bundle, selections = {}) => {
    let deltaTotal = 0;
    const resolvedItems = [];
    const configParts = [];

    (bundle.items || []).forEach((bundleItem) => {
      const product = products.find((p) => p.id === bundleItem.id);
      const basePrice = Number(
        bundleItem.basePrice ?? product?.price ?? bundleItem.price ?? 0
      );
      const qtyNeeded = Math.max(1, Number(bundleItem.quantity) || 1);
      const selectionValue = selections[bundleItem.id];
      let selectionArray = [];
      if (isAnyVariantItem(bundleItem)) {
        selectionArray = Array.isArray(selectionValue)
          ? selectionValue
          : Array(qtyNeeded).fill(selectionValue || null);
      } else if (bundleItem.variantId) {
        selectionArray = Array(qtyNeeded).fill(bundleItem.variantId);
      } else {
        selectionArray = Array(qtyNeeded).fill(selectionValue || null);
      }

      const counts = {};
      selectionArray.slice(0, qtyNeeded).forEach((vidRaw) => {
        const vid = vidRaw || null;
        counts[vid] = (counts[vid] || 0) + 1;
      });

      Object.entries(counts).forEach(([vidKey, count]) => {
        const variant = product?.variants?.find(
          (v) => (v.id || v.variantId) === vidKey
        );
        const variantName = variant ? getVariantLabel(variant) : bundleItem.variantName || '';
        const selectedOptions = variant?.options || bundleItem.selectedOptions || bundleItem.options || [];
        const varDelta = computeVariantDelta(product, variant);
        deltaTotal += varDelta * count;

        resolvedItems.push({
          ...bundleItem,
          variantId: vidKey || null,
          variantName,
          selectedOptions,
          quantity: count,
          variantPriceDelta: varDelta,
        });
        configParts.push(`${bundleItem.id}:${vidKey || 'any'}:${count}`);
      });
    });

    const configKey = configParts.join('|') || 'default';

    return {
      resolvedItems,
      deltaTotal,
      configKey,
    };
  };

  const addBundleLineToCart = (bundle, resolvedItems, deltaTotal, configKey, currentNote = '') => {
    setCart((prev) => {
      const existingItem = prev.find(
        (cartItem) =>
          cartItem.id === bundle.id &&
          (cartItem.bundleConfigKey || 'default') === (configKey || 'default')
      );
      const bundlePrice = Number(bundle.price || 0) + deltaTotal;
      const newCart = existingItem
        ? prev.map((cartItem) =>
            cartItem.id === bundle.id &&
            (cartItem.bundleConfigKey || 'default') === (configKey || 'default')
              ? {
                  ...cartItem,
                  qty: (cartItem.qty || 0) + 1,
                  items: resolvedItems,
                  price: bundlePrice,
                  bundleConfigKey: configKey || 'default',
                  variantPriceDelta: deltaTotal,
                  note: cartItem.note || currentNote,
                }
              : cartItem
          )
        : [
            ...prev,
            {
              ...bundle,
              qty: 1,
              items: resolvedItems,
              price: bundlePrice,
              bundleConfigKey: configKey || 'default',
              variantPriceDelta: deltaTotal,
              note: currentNote,
            },
          ];
      return newCart;
    });
  };

  const handleConfirmBundleVariants = () => {
    const { bundle, selections, note } = bundleVariantContext;
    if (!bundle) return;

    const missingSelection = (bundle.items || []).some((bundleItem) => {
      if (!isAnyVariantItem(bundleItem)) return false;
      const needed = Math.max(1, Number(bundleItem.quantity) || 1);
      const sel = selections[bundleItem.id];
      if (Array.isArray(sel)) {
        return sel.slice(0, needed).some((s) => !s);
      }
      return !sel;
    });
    if (missingSelection) {
      toast.error('Please choose a variant for each bundle item.');
      return;
    }

    const { resolvedItems, deltaTotal, configKey } = buildBundleConfig(bundle, selections);
    const candidateBundle = { ...bundle, items: resolvedItems };
    if (wouldExceedBundleAdd(candidateBundle, 1)) {
      toast.error('Adding this bundle would exceed available stock for some items');
      return;
    }

    const outOfStockItem = resolvedItems.find((bundleItem) => {
      const availableStock = getAvailableStock(bundleItem.id, bundleItem.variantId || null);
      const reserved = getReservedStock(bundleItem.id, bundleItem.variantId || null, cart);
      return reserved + (bundleItem.quantity || 1) > availableStock;
    });
    if (outOfStockItem) {
      toast.error(`Not enough stock for ${outOfStockItem.name} in bundle`);
      return;
    }

    addBundleLineToCart(
      bundle,
      resolvedItems,
      deltaTotal,
      configKey,
      note || itemNotes[bundle.id] || ''
    );
    setBundleVariantModalOpen(false);
    setBundleVariantContext({ bundle: null, selections: {} });
  };

  const loadProducts = async () => {
    if (!user) return;
    
    // Load products and bundles in parallel
    const [productsSnap, bundlesSnap] = await Promise.all([
      getDocs(collection(db, 'companies', companyId, 'inventory', 'data', 'products')),
      getDocs(collection(db, 'companies', companyId, 'inventory', 'data', 'bundles'))
    ]);
    
    const loadedProducts = productsSnap.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      type: doc.data().type || 'product'
    }));
    
    // Process bundles and update their quantities in Firestore
    const bundlePromises = [];
    const loadedBundles = [];
    
    for (const bundleDoc of bundlesSnap.docs) {
      const bundleData = bundleDoc.data();
      let minQty = calculateBundleQuantity(
        { ...bundleData, id: bundleDoc.id },
        loadedProducts
      );
      
      // Update the bundle's quantity in Firestore
      const bundleRef = doc(db, 'companies', companyId, 'inventory', 'data', 'bundles', bundleDoc.id);
      bundlePromises.push(updateDoc(bundleRef, { quantity: minQty }));
      
      loadedBundles.push({
        id: bundleDoc.id,
        ...bundleData,
        type: 'bundle',
        quantity: minQty
      });
    }
    
    // Wait for all bundle updates to complete
    await Promise.all(bundlePromises);
    
    setProducts(loadedProducts);
    setBundles(loadedBundles);
  };

  // Load cart from sessionStorage on component mount
  useEffect(() => {
    const savedCart = sessionStorage.getItem("cashierCart");
    if (savedCart) {
      setCart(JSON.parse(savedCart));
    }
  }, []);

  // Load notes from sessionStorage when component mounts
  useEffect(() => {
    const savedNotes = sessionStorage.getItem("cashierNotes");
    if (savedNotes) {
      setItemNotes(JSON.parse(savedNotes));
    }
  }, []);

  // Save notes to sessionStorage whenever they change
  useEffect(() => {
    if (Object.keys(itemNotes).length > 0) {
      sessionStorage.setItem("cashierNotes", JSON.stringify(itemNotes));
    } else {
      sessionStorage.removeItem("cashierNotes");
    }
  }, [itemNotes]);

  // Save cart to sessionStorage whenever it changes
  useEffect(() => {
    if (cart.length > 0) {
      sessionStorage.setItem("cashierCart", JSON.stringify(cart));
    } else {
      sessionStorage.removeItem("cashierCart");
    }
  }, [cart]);

  const handlePaymentConfirm = async () => {

    if (!user || cart.length === 0 || !mode) {
      toast.error('Please ensure mode selected and cart is not empty.');
      return;
    }
  
    if (paymentMethod === 'Cash') {
      const cash = parseFloat(cashInput);
      if (isNaN(cash)) {
        toast.error('Please enter cash amount.');
        return;
      }
      if (cash < total) {
        toast.error('Received cash must be equal to or greater than the total.');
        return;
      }
    }
    if (paymentMethod === 'Debit/Credit' && !cardRefNum) {
      toast.error('Please enter reference number.');
      return;
    }
    setConfirmLoading(true);
    try {
      // Get the user's public ID first before any other operations
      const userDoc = await getDoc(doc(db, 'companies', companyId));
      const fetchedPublicId = userDoc.data()?.publicId;

      if (!fetchedPublicId) {
        console.error('No public ID found for user');
        toast.error('Error processing receipt. Please try again.');
        setConfirmLoading(false);
        return;
      }
      
      setPublicId(fetchedPublicId);

      const queueNo = null;
      const receiptNo = await getNextCounter(companyId, 'receiptCounter');
      setReceiptNumber(`#${receiptNo}`);

      const sanitizeItem = (raw) => {
        const base = {
          id: raw.id,
          name: raw.name || '',
          price: Number(raw.price || 0),
          qty: raw.qty ?? raw.quantity ?? 1,
          variantId: raw.variantId ?? null,
          variantName: raw.variantName ?? null,
          type: raw.type || 'product',
          note: raw.note || '',
          variantPriceDelta: Number(raw.variantPriceDelta || 0),
          selectedOptions: raw.selectedOptions || raw.options || [],
        };
        if (base.type === 'bundle' && Array.isArray(raw.items)) {
          base.items = raw.items.map((sub) => ({
            id: sub.id,
            name: sub.name || '',
            quantity: sub.quantity || 1,
            variantId: sub.variantId ?? null,
            variantName: sub.variantName ?? null,
            selectedOptions: sub.selectedOptions || sub.options || [],
            variantPriceDelta: Number(sub.variantPriceDelta || 0),
          }));
        }
        return base;
      };

      const cleanedItems = cart.map(sanitizeItem);
  
      const order = {
      customerName: customerName?.trim() || '',
      receiptNumber: receiptNo,
      queueNumber: queueNo,
      mode: mode || 'Take away',
      paymentMethod: paymentMethod,
      items: cleanedItems,
      subtotal: subtotal,
      sst6Amount: sst6Amount,
      sst8Amount: sst8Amount,
      serviceTax10Amount: serviceTax10Amount,
      serviceTax10Rate: Number(serviceTax10Rate || 0),
      companyRegType: companySnap.regType || '',
      companyRegNumber: companySnap.regNumber || '',
        discount: selectedDiscount?.name || "-",
        discountAmount: discountAmount,
      rounding: rounding.toFixed(2),
      total,
      received: effectiveReceived,
      balance,
      timestamp: new Date().toISOString(),
      paymentStatus: "Paid",
      cashierId: selectedCashier || "admin",
      cardRefNum: cardRefNum ,
    };

      const orderDocRef = await addDoc(collection(db, 'companies', companyId, 'orders'), order);

       const publicReceipt = {
        companyLogo: companySnap.logoUrl,
        companyName: companySnap.companyName,
        companyStreet: companySnap.street,
        companyCity: companySnap.city,
        companyPostcode: companySnap.postcode,
        companyState: companySnap.state,
        companyCountry: companySnap.country,
        companyCountryCode: companySnap.countryCode,
        companyPhone: companySnap.phone,
        companyWebsite: companySnap.website,
        companySSTNumber: companySnap.sstNumber,
        companyRegType: companySnap.regType || '',
        companyRegNumber: companySnap.regNumber || '',
        receiptNumber: receiptNo,
        queueNumber: queueNo,
        mode: mode || "Take away",
        paymentMethod,
        items: cleanedItems,
        subtotal,
        sst6Amount,
        sst8Amount,
        serviceTax10Amount,
        serviceTax10Rate: Number(serviceTax10Rate || 0),
        discount: selectedDiscount?.name || '-',
        discountAmount,
        rounding: rounding.toFixed(2),
        total,
        received: effectiveReceived,
        balance,
        timestamp: new Date().toISOString(),
        paymentStatus: 'Paid',
        cashierId: selectedCashier || 'admin',
        cardRefNum,
        footer: companyInfo.receiptFooter,
        thankYouNote: companyInfo.thankYouNote,
      };


      // Save receipt to public collection under the user's public ID
      await setDoc(doc(db, 'publicUsers', fetchedPublicId, 'receipts', orderDocRef.id), publicReceipt);
      // Mark discount code as redeemed if applicable
      if (selectedDiscount?.id && (selectedDiscount.appliedCode || appliedCode)) {
        const usedCode = (selectedDiscount.appliedCode || appliedCode).toUpperCase();
        const discountRef = doc(db, 'companies', companyId, 'companySettings', 'discounts', 'data', selectedDiscount.id);
        try {
          await runTransaction(db, async (transaction) => {
            const snap = await transaction.get(discountRef);
            if (!snap.exists()) return;
            const data = snap.data();
            const existingRedeemed = data.redeemedCodes || [];
            const codes = data.codes || (data.code ? [data.code] : []);
            const generated = data.generated;
            const voucherLimit = Number(data.voucherCount || codes.length || 0);
            // For generated codes: ensure unique redemption per code
            if (generated) {
              if (existingRedeemed.map((c) => c.toUpperCase()).includes(usedCode)) return;
              transaction.update(discountRef, { redeemedCodes: [...existingRedeemed, usedCode] });
            } else {
              // Non-generated: allow multiple uses up to voucher count
              if (voucherLimit && existingRedeemed.length >= voucherLimit) return;
              transaction.update(discountRef, { redeemedCodes: [...existingRedeemed, usedCode] });
            }
          });
          // update local state so further applications respect usage
          setDiscountOptions((prev) =>
            prev.map((d) =>
              d.id === selectedDiscount.id
                ? { ...d, redeemedCodes: [...(d.redeemedCodes || []), usedCode] }
                : d
            )
          );
          setSelectedDiscount((prev) =>
            prev && prev.id === selectedDiscount.id
              ? { ...prev, redeemedCodes: [...(prev.redeemedCodes || []), usedCode] }
              : prev
          );
        } catch (err) {
          console.error('Failed to update redeemed code:', err);
        }
      }

      // Preorder flow removed
      
      // refresh latest stock before deduction
      try {
        await loadProducts();
      } catch (err) {
        console.warn('Could not refresh products before checkout:', err);
      }

      // ✅ Deduct inventory
      const paidOrder = selectedOnlineOrderData;
      const isOnlineOrder = Boolean(paidOrder);
      for (const item of cart) {
        // Calculate how many of this line were in the original online order (if any)
        const savedQty = isOnlineOrder ? (savedOrderRef.current[item.id] || 0) : 0;
        const cartQty = item.qty ?? item.quantity ?? 1;
        const deltaQty = cartQty - savedQty;

        // For offline orders, savedQty is 0 so deltaQty === cartQty
        // For online orders, only deduct if cashier added extra quantity beyond original
        if (deltaQty <= 0) continue;

        if (bundles.some(b => b.id === item.id)) {
          // Deduct each item in the bundle based on additional quantity
          for (const bundleItem of item.items || []) {
            const productRef = doc(db, 'companies', companyId, 'inventory', 'data', 'products', bundleItem.id);
            
            await runTransaction(db, async (transaction) => {
              const prodSnap = await transaction.get(productRef);
              if (!prodSnap.exists()) {
                console.warn(`Product ${bundleItem.id} in bundle no longer exists`);
                return; // Skip if product doesn't exist
              }

              const prodData = prodSnap.data();
              const quantityToDeduct = bundleItem.quantity * deltaQty;
              if (bundleItem.variantId) {
                const variantsArr = prodData.variants || [];
                const idx = variantsArr.findIndex(v => v.id === bundleItem.variantId);
                if (idx === -1) return;
                const currentQty = variantsArr[idx].qty || 0;
                if (currentQty < quantityToDeduct) {
                  throw new Error(`Not enough stock for ${bundleItem.name} in bundle ${item.name}`);
                }
                variantsArr[idx] = { ...variantsArr[idx], qty: currentQty - quantityToDeduct };
                const totalQty = variantsArr.reduce((s, v) => s + Number(v.qty || 0), 0);
                transaction.update(productRef, { variants: variantsArr, qty: totalQty });
              } else {
                const currentQty = prodData.qty || 0;
                if (currentQty < quantityToDeduct) {
                  throw new Error(`Not enough stock for ${bundleItem.name} in bundle ${item.name}`);
                }
                transaction.update(productRef, {
                  qty: currentQty - quantityToDeduct,
                });
              }
            });
          }
        } else {
          // Handle regular products
          const productRef = doc(db, 'companies', companyId, 'inventory', 'data', 'products', item.id);

          await runTransaction(db, async (transaction) => {
            const prodSnap = await transaction.get(productRef);
            if (!prodSnap.exists()) {
              console.warn(`Product ${item.id} no longer exists`);
              return; // Skip if product doesn't exist
            }

            const prodData = prodSnap.data();
            if (item.variantId) {
              const variantsArr = prodData.variants || [];
              const idx = variantsArr.findIndex(v => v.id === item.variantId);
              if (idx === -1) return;
              const currentQty = variantsArr[idx].qty || 0;
              if (currentQty < deltaQty) {
                throw new Error(`Not enough stock for ${item.name}`);
              }
              variantsArr[idx] = { ...variantsArr[idx], qty: currentQty - deltaQty };
              const totalQty = variantsArr.reduce((s, v) => s + Number(v.qty || 0), 0);
              transaction.update(productRef, { variants: variantsArr, qty: totalQty });
            } else {
              const currentQty = prodData.qty;
              if (currentQty < deltaQty) {
                throw new Error(`Not enough stock for ${item.name}`);
              }
              transaction.update(productRef, {
                qty: currentQty - deltaQty,
              });
            }
          });
        }
      }
  
      // Update queue order status to paid and delete if it's an online order
      // Online order cleanup removed
  
      setReceiptNumber(`#${receiptNo}`);
      setFinalReceipt({ ...order, id: orderDocRef.id });
      setShowPaymentModal(false);
      setShowReceipt(true);
      setCart([]);
      sessionStorage.removeItem('cashierCart');
      setItemNotes({});
      sessionStorage.removeItem('cashierNotes');
      setCashInput('');
  setSelectedDiscount(null);
  setAppliedCode('');
  setDiscountError('');
  setDiscountCodeInput('');
  setMode('');
  setCustomerName('');
      if (showOrder) setShowOrder(false);
      await loadQueueOrders(); // Refresh queue list
      if (isOnlineOrder && paidOrder) {
        try {
          const preorderRef = doc(db, 'companies', companyId, 'preorders', paidOrder.id);
          // Update payment status to paid but keep the order in kitchen display
          await updateDoc(preorderRef, {
            paymentStatus: 'paid',
            updatedAt: serverTimestamp()
          });
          
          // Remove from local list and reset selection
          setOnlineOrders((prev) => prev.filter((o) => o.id !== paidOrder.id));
          setSelectedOnlineOrderId('');
          setSelectedOnlineOrderData(null);
          await loadOnlineOrders();
        } catch (err) {
          console.error('Failed to update online order status:', err);
        }
      }
  
    } catch (error) {
      console.error('Payment failed:', error);
      toast.error('Payment failed. Please try again.');
    }
  
    setConfirmLoading(false);
  };
  

  const handlePrint = () => {
    const printContent = receiptRef.current?.innerHTML;
    if (!printContent) {
      toast.error('Receipt content not ready.');
      return;
    }
    const printWindow = window.open('', 'PRINT', 'width=300,height=600');
    if (!printWindow) {
      toast.error('Popup blocked! Please allow popups for this site.');
      return;
    }
    const style = `
  <style>
    @media print {
      body {
        margin: 0 auto;
      }
    }
    body {
      margin: 0 auto;
      padding: 0 10px;
      font-family: monospace;
      font-size: 14px;
    }
    .receipt { 
      width: 280px;
      padding: 0 10px;
      margin: 0 auto;
      font-size: 14px;
      font-family: monospace;
    }
    .center {
      text-align: center;
    }
    .bold {
      font-weight: bold;
    }
    .line {
      border-top: 1px dashed #000;
      margin: 5px 0;
    }
    .flex {
      display: flex;
      justify-content: space-between;
    }
    img {
      max-height: 40px;
      margin: 0 auto 10px;
      display: block;
    }
    .flex span:first-child {
      text-align: left;
      flex: 1;
    }
    .flex span:last-child {
      text-align: right;
      min-width: 60px;
    }
    .text-center {
      text-align: center;
    }
  </style>
`;
    printWindow.document.write(`
      <html>
        <head><title>Receipt</title>${style}</head>
        <body>
          <div class="receipt">${printContent}</div>
          <script>
            window.onload = function() {
              window.focus();
              window.print();
              window.onafterprint = function() { window.close(); };
            };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const handleKeypadInput = (key) => {
    if (key === '⌫') {
      setCashInput((prev) => prev.slice(0, -1));
    } else {
      setCashInput((prev) => {
        if (key === '.' && prev.includes('.')) return prev;
        return prev + key.toString();
      });
    }
  };

  const handlePriceChange = (itemId, newPrice) => {
    if (isNaN(newPrice) || newPrice < 0) return;
    setCart((prevCart) =>
      prevCart.map((item) =>
        item.id === itemId ? { ...item, price: newPrice } : item
      )
    );
    const updatedCart = cart.map((item) =>
      item.id === itemId ? { ...item, price: newPrice, note: itemNotes[item.id] || '' } : item
    );
    sessionStorage.setItem("cashierCart", JSON.stringify(updatedCart));
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      loadProducts();
    }, 1000);
    return () => clearTimeout(timer);
  }, [user]);

  useEffect(() => {
    if (!user) return;
    (async () => {
      await loadProducts();
      const settingsSnap = await getDoc(doc(db, 'companies', companyId, 'companySettings', 'info'));
      if (settingsSnap.exists()) {
        const data = settingsSnap.data();
        // Dynamic tax logic
  if (data.taxEnabled) {
    if (data.sst6Enabled) {
      setSst6Enabled(true);
      setSst6Rate(6);
      setSst6Label('SST (6%)');
    } else {
      setSst6Enabled(false);
      setSst6Rate(0);
      setSst6Label('');
    }
    if (data.sst8Enabled) {
      setSst8Enabled(true);
      setSst8Rate(8);
      setSst8Label('SST (8%)');
    } else {
      setSst8Enabled(false);
      setSst8Rate(0);
      setSst8Label('');
    }
    if (data.serviceTax10Enabled) {
      setServiceTax10Enabled(true);
      setServiceTax10Rate(Number(data.serviceTax10Rate || 0));
    } else {
      setServiceTax10Enabled(false);
      setServiceTax10Rate(0);
    }
  }
        setCompanyInfo(data);
        setStallType(data.stallType); 
      }
      const companyProfileSnap = await getDoc(doc(db, 'companies', companyId, 'companyProfile', 'info'));
      if (companyProfileSnap.exists()) setCompanySnap(companyProfileSnap.data());
      const discountsSnap = await getDocs(collection(db, 'companies', companyId, 'companySettings', 'discounts', 'data'));
      setDiscountOptions(discountsSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
      await loadOnlineOrders();
    })();
  }, [user]);

  useEffect(() => {
    const fetchCashiers = async () => {
      const docRef = doc(db, 'companies', companyId, 'companySettings', 'main');
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        const cashierList = data.cashiers || [];
        setCashiers(cashierList);
        setSelectedCashier(cashierList.length > 0 ? cashierList[0].id : 'admin');
      } else {
        setSelectedCashier('admin');
      }
    };
    fetchCashiers();
  }, [user.uid, companyId]);

  const isFnb = (stallType || '').toString().toLowerCase() === 'fnb';
  const defaultMode = isFnb ? 'Dine In' : 'Retail';
  const modeOptions = isFnb ? ['Dine In', 'Take away', 'Online'] : ['Retail', 'Online'];

  // Auto-select mode based on stall type
  useEffect(() => {
    if (!mode) {
      setMode(defaultMode);
    }
  }, [defaultMode, mode]);
  const handleRemoveItem = async (id, variantId = null, lineIndex = null) => {
    const wasSavedQty = savedOrderRef.current[id] || 0;
    const removedItems = [];
    let updatedCart = cart.filter((item, idx) => {
      const match =
        (lineIndex !== null && idx === lineIndex) ||
        (lineIndex === null && item.id === id && (variantId ? item.variantId === variantId : true));
      if (match) removedItems.push(item);
      return !match;
    });
    setItemNotes(prev => {
      const newNotes = { ...prev };
      delete newNotes[id];
      return newNotes;
    });
  
    // If it was part of saved order, restore inventory
    const stillHasLine = updatedCart.some((item) => item.id === id && (variantId ? item.variantId === variantId : true));
    if (wasSavedQty > 0 && !stillHasLine) {
      try {
        const productRef = doc(db, 'companies', companyId, 'inventory', 'data', 'products', id);
        await runTransaction(db, async (transaction) => {
          const prodSnap = await transaction.get(productRef);
          if (!prodSnap.exists()) return;
          const currentQty = prodSnap.data().qty;
          transaction.update(productRef, { qty: currentQty + wasSavedQty });
        });
  
        delete savedOrderRef.current[id];
      } catch (err) {
        console.error("Failed to restore inventory:", err);
        toast.error("Failed to update inventory stock.");
      }
    }
    setCart(updatedCart);
    sessionStorage.setItem("cashierCart", JSON.stringify(updatedCart));

const savedItemIds = Object.keys(savedOrderRef.current);
const stillHasSavedItems = updatedCart.some((item) => savedItemIds.includes(item.id));

  }


// ============================
// 🔹 State + Refs
// ============================
const scannerRef = useRef(null);
const scannerActiveRef = useRef(false);
const readerRef = useRef(null);
const [showScanner, setShowScanner] = useState(false);
const [barcode, setBarcode] = useState("");
const [lastScannedBarcode, setLastScannedBarcode] = useState("");
const barcodeInputRef = useRef(null);
const searchInputRef = useRef(null);
const [showSearch, setShowSearch] = useState(false);
const [variantModalOpen, setVariantModalOpen] = useState(false);
const [variantModalProduct, setVariantModalProduct] = useState(null);
const [variantModalVariantId, setVariantModalVariantId] = useState('');
const [bundleVariantModalOpen, setBundleVariantModalOpen] = useState(false);
const [bundleVariantContext, setBundleVariantContext] = useState({ bundle: null, selections: {} });

// ============================
// 🔹 Handle Input Changes (Scanner detection)
// ============================
const handleBarcodeInput = (e) => {
  const value = e.target.value;
  setBarcode(value);

  // Detect fast input typical from scanner
  if (value.length > 5) {
    clearTimeout(window.barcodeTimeout);
    window.barcodeTimeout = setTimeout(() => {
      processBarcode(value, true); // "auto" = true means scanner
    }, 1500);
  }
};

// ============================
// 🔹 Process Scanned or Typed Barcode
// ============================
const processBarcode = (barcodeValue, auto = false) => {
  const cleanBarcode = barcodeValue.toString().trim();
  if (!cleanBarcode) return;

  const handleNotFound = () => toast.error("Item not found");

  (async () => {
    try {
      const ref = doc(db, 'companies', companyId, 'barcodes', cleanBarcode);
      const snap = await fetchDoc(ref);
      if (!snap.exists()) {
        handleNotFound();
      } else {
        const data = snap.data();
        const prod = products.find(p => p.id === data.productId);
        if (!prod) {
          handleNotFound();
          return;
        }
        if (data.variantId) {
          addVariantToCart(prod, data.variantId);
        } else {
          handleAddToCart(prod);
        }
      }
    } catch (err) {
      console.error('Barcode lookup failed', err);
      handleNotFound();
    }
  })();

  // ✅ Reset barcode input
  setBarcode("");
  setLastScannedBarcode(cleanBarcode);
  barcodeInputRef.current?.focus();

  // ✅ Close camera modal (if exists)
  if (!auto && typeof closeCameraModal === "function") {
    closeCameraModal();
  }
};
      // 🔹 Camera-based barcode scanning
      useEffect(() => {
        if (!showScanner) return;
    
        const html5QrCode = new Html5Qrcode("reader");
        scannerRef.current = html5QrCode;
    
        const onScanSuccess = (decodedText) => {
          if (decodedText) {
            setBarcode(decodedText);
            setShowScanner(false);
            barcodeInputRef.current?.focus(); // Focus the barcode input
          }
        };
    
        html5QrCode
          .start(
            { facingMode: "environment" },
            {
              fps: 8,
              qrbox: { width: 350, height: 250 },
              formatsToSupport: [
                Html5QrcodeSupportedFormats.EAN_13,
                Html5QrcodeSupportedFormats.CODE_128,
                Html5QrcodeSupportedFormats.CODE_39,
                Html5QrcodeSupportedFormats.UPC_A,
                Html5QrcodeSupportedFormats.UPC_E,
                Html5QrcodeSupportedFormats.QR_CODE,
              ],
            },
            onScanSuccess,
            (errorMsg) => {
              // Ignore "no barcode detected" logs — they’re normal
            }
          )
          .then(() => {
            scannerActiveRef.current = true;
          })
          .catch((err) => {
            scannerActiveRef.current = false;
            console.error("Camera start failed:", err);
            if (err?.name === "NotAllowedError") {
              toast.error("Camera permission denied. Please enable it in your browser settings.");
            } else {
              toast.error("Unable to start camera. Please try again.");
            }
            setShowScanner(false);
          });
    
        return () => {
          if (!scannerActiveRef.current) {
            try {
              const clearResult = html5QrCode.clear();
              if (clearResult && typeof clearResult.catch === "function") {
                clearResult.catch(() => {});
              }
            } catch (err) {
              // ignore clear errors
            }
            return;
          }
          html5QrCode
            .stop()
            .then(() => {
              scannerActiveRef.current = false;
              try {
                return html5QrCode.clear();
              } catch (err) {
                return null;
              }
            })
            .catch(() => {}); // ignore stop errors
        };
      }, [showScanner]);

const [customerDisplay, setCustomerDisplay] = useState(null);
const [customerDisplayInitialized, setCustomerDisplayInitialized] = useState(false);
const allowCustomerDisplay = companyInfo?.customerDisplayEnabled === true;

useEffect(() => {
  // If disabled, close any existing window and skip opening
  if (!allowCustomerDisplay) {
    if (window.customerDisplay && !window.customerDisplay.closed) {
      window.customerDisplay.close();
    }
    window.customerDisplay = null;
    setCustomerDisplay(null);
    setCustomerDisplayInitialized(false);
    return;
  }

  if (customerDisplayInitialized) return;

  // Check if a display window already exists and is still open
  if (window.customerDisplay && !window.customerDisplay.closed) {
    setCustomerDisplay(window.customerDisplay);
    window.customerDisplay.focus();
    setCustomerDisplayInitialized(true);
    return;
  }

  // Otherwise, open a new window
  const display = window.open("/customer-display", "_blank");
  if (display) {
    display.focus();
    window.customerDisplay = display;
    setCustomerDisplay(display);
    setCustomerDisplayInitialized(true);
  }

  // Optional cleanup if cashier tab closes
  const handleUnload = () => {
    if (window.customerDisplay && !window.customerDisplay.closed) {
      window.customerDisplay.close();
    }
  };

  window.addEventListener("beforeunload", handleUnload);

  return () => window.removeEventListener("beforeunload", handleUnload);
}, [allowCustomerDisplay, customerDisplayInitialized]);

  useEffect(() => {
  // Build cart with variant deltas for customer display
  const mappedCart = cart.map((item) => {
    const productRef = products.find((p) => p.id === item.id);
    const variantObj = productRef?.variants?.find(
      (v) => (v.id || v.variantId) === item.variantId
    );
    const resolvedDelta =
      Number(item.variantPriceDelta || 0) ||
      (variantObj ? computeVariantDelta(productRef, variantObj) : 0);

    const mappedBundleItems = Array.isArray(item.items)
      ? item.items.map((sub) => {
          const subProduct = products.find((p) => p.id === sub.id);
          const subVariant = subProduct?.variants?.find(
            (v) => (v.id || v.variantId) === sub.variantId
          );
          const subDelta =
            Number(sub.variantPriceDelta || 0) ||
            (subVariant ? computeVariantDelta(subProduct, subVariant) : 0);
          return { ...sub, variantPriceDelta: subDelta };
        })
      : item.items;

    return {
      ...item,
      variantPriceDelta: resolvedDelta,
      items: mappedBundleItems,
    };
  });

  bc.postMessage({
    type: "UPDATE_INFO",
    info: {
      mode: mode,
      subtotal: subtotal || 0,
      tax: taxAmount || 0, // ✅ use taxAmount
      discount: discountAmount || 0, // ✅ use discountAmount
      rounding: rounding || 0,
      total: total || 0,
    },
  });

  bc.postMessage({ type: "UPDATE_CART", cart: mappedCart });
}, [mode, subtotal, cart, discountAmount, taxAmount, rounding, total, products]);

useEffect(() => {
  if (paymentMethod === "E-wallet") {
    bc.postMessage({ type: "SHOW_DUITNOW_QR"});
  } else {
    bc.postMessage({ type: "SHOW_POSTER" });
  }
}, [paymentMethod]);

  const handleToggleSearch = () => {
    setShowSearch((prev) => {
      const next = !prev;
      if (next) setTimeout(() => searchInputRef.current?.focus(), 60);
      return next;
    });
  };

  const renderSearchInput = () => (
    <div className="relative w-full">
      <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400">
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M21 21l-4.35-4.35M10 18a8 8 0 100-16 8 8 0 000 16z"
          />
        </svg>
      </span>

      <input
        ref={searchInputRef}
        type="text"
        placeholder="Search by SKU, name or barcode"
        className="w-full bg-[#1f1f1f] border border-gray-700 text-white pl-10 pr-4 py-2 rounded focus:ring-2 focus:ring-purple-500 focus:outline-none"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
    </div>
  );

  return (
    <div className="text-white  min-h-screen md:px-1">
      <Toaster
        position="top-center"
        gutter={12}
        containerStyle={{ zIndex: 99999 }}
        toastOptions={{
          style: { zIndex: 99999, background: '#1f1f1f', color: '#fff' },
        }}
      />
      {!showOrder && (
        <button
          onClick={() => setShowOrder(true)}
          className="fixed bottom-5 right-4 z-50 bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-full shadow-lg lg:hidden"
          aria-label="Toggle Order Panel"
        >
          Order ({cart.length})
        </button>
      )}

      <div className="flex lg:flex-row flex-col h-screen max-h-screen overflow-hidden bg-black">
        {/* Left Side: Product Selection */}
        <div className={`bg-black w-full flex flex-col ${showOrder ? 'hidden' : 'block'} lg:block lg:w-3/5 overflow-y-auto`}>
          <div className="px-3 md:px-4 pt-4 max-w-full overflow-x-hidden">
            <div className="flex items-center gap-3 mb-3">
              <button onClick={() => navigate(-1)} className="text-sm flex items-center gap-1 text-gray-300 hover:text-white">
                <span className="text-lg">←</span> Back
              </button>
            </div>

            <div className="flex flex-col gap-3 mb-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <div>
                  <h1 className="text-3xl font-bold leading-tight">Cashier</h1>
                  <div className="text-gray-400">Today, {formatDate(new Date())}</div>
                </div>
                <button
                  type="button"
                  onClick={handleToggleSearch}
                  className="p-2 rounded-full bg-[#1f1f1f] border border-gray-700 text-gray-200 hover:text-white hover:border-purple-500 transition-colors"
                  aria-label="Toggle search"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M10 18a8 8 0 100-16 8 8 0 000 16z" />
                  </svg>
                </button>
              </div>
              {showSearch && (
                <div className="w-full">
                  {renderSearchInput()}
                </div>
              )}
            </div>
            {/* Responsive Dropdowns: Stack on mobile, side-by-side on tablet+ */}
            <div className="space-y-4 mb-4">
              {/* Cashier & Online order selectors */}
              <div className="flex flex-wrap gap-4 md:flex-nowrap">
                <div className="flex-1 min-w-[200px]">
                  <label className="block text-sm text-gray-400 mb-1">Cashier</label>
                  <select
                    className="w-full bg-[#1f1f1f] text-white rounded px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:outline-none"
                    value={selectedCashier}
                    onChange={(e) => setSelectedCashier(e.target.value)}
                  >
                    {cashiers.length === 0 ? (
                      <option value="admin">Admin</option>
                    ) : (
                      cashiers.map((cashier) => (
                        <option key={cashier.id} value={cashier.id}>
                          {cashier.name}
                        </option>
                      ))
                    )}
                  </select>
                </div>
                <div className="flex-1 min-w-[200px]">
                  <label className="block text-sm text-gray-400 mb-1">Online Order</label>
                  <select
                    className="w-full bg-[#1f1f1f] text-white rounded px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:outline-none"
                    value={selectedOnlineOrderId}
                    onChange={(e) => handleSelectOnlineOrder(e.target.value)}
                  >
                    <option value="">Select an online order</option>
                    {onlineOrders.map((o) => {
                      const queue = o.queueNumber ? `#${String(o.queueNumber).padStart(3, '0')} ` : '';
                      const customer = o.customerName || o.customerInfo?.name || 'Customer';
                      const phone = o.customerPhone || o.customerInfo?.phone || '';
                      return (
                        <option key={o.id} value={o.id}>
                          {queue}{customer}{phone ? ` (${phone})` : ''} - {o.id}
                        </option>
                      );
                    })}
                  </select>
                </div>
              </div>
            </div>
<div className="flex flex-col gap-3 mb-4">
{/* Barcode Input */}
<div className="flex items-center gap-2 w-full">
  <div className="relative flex-1">
    <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400">
      {/* Better Barcode Icon */}
      <svg
        className="w-5 h-5"
        viewBox="0 0 24 24"
        fill="currentColor"
        aria-hidden="true"
      >
        {/* multiple vertical bars to mimic a barcode */}
        <rect x="2"  y="4" width="1" height="16" />
        <rect x="4"  y="4" width="2" height="16" />
        <rect x="7"  y="4" width="1" height="16" />
        <rect x="9"  y="4" width="3" height="16" />
        <rect x="13" y="4" width="1" height="16" />
        <rect x="15" y="4" width="2" height="16" />
        <rect x="18" y="4" width="1" height="16" />
        <rect x="20" y="4" width="1" height="16" />
      </svg>
    </span>

    <input
      ref={barcodeInputRef}
      className="w-full bg-[#1f1f1f] border border-gray-700 text-white pl-10 pr-4 py-2 rounded focus:ring-2 focus:ring-purple-500 focus:outline-none"
      value={barcode}
      onChange={handleBarcodeInput}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          processBarcode(barcode, false);
        }
      }}
      placeholder="Scan or enter barcode"
      autoFocus
      autoComplete="off"
      autoCorrect="off"
      spellCheck="false"
    />
  </div>

    {/* Camera Button */}
    <button
      type="button"
      onClick={() => setShowScanner(true)}
      className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded flex items-center justify-center gap-2 whitespace-nowrap transition-colors"
    >
      <svg
        className="w-5 h-5"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
        />
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
        />
      </svg>
      <span className="hidden sm:inline">Camera</span>
    </button>
</div>
</div>

{/* Camera Modal */}
{showScanner && (
  <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-80 z-[12000]">
    <div className="bg-white rounded-lg p-4 w-11/12 max-w-md">
      <h3 className="text-lg font-bold text-center text-gray-800 mb-3">
        Scan Barcode
      </h3>
      <div
        id="reader"
        ref={readerRef}
        className="w-full rounded overflow-hidden"
        style={{ height: "300px" }}
      />
      <button
        type="button"
        onClick={() => setShowScanner(false)}
        className="mt-4 w-full bg-red-600 hover:bg-red-700 text-white py-2 rounded"
      >
        Close Scanner
      </button>
    </div>
  </div>
)}
{variantModalOpen && variantModalProduct && (
  <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-70 z-[12500] p-4">
    <div className="bg-[#1f1f1f] text-white w-full max-w-lg rounded-lg shadow-lg p-4 space-y-3">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Select Variant</h3>
        <button onClick={() => setVariantModalOpen(false)} className="text-gray-300 hover:text-white">✕</button>
      </div>
      <div className="text-sm text-gray-300">{variantModalProduct.name}</div>
      <div className="space-y-2 max-h-96 overflow-y-auto">
        {variantModalProduct.variants?.map((v) => {
          const variantName = (v.options || []).map((o) => `${o.group}: ${o.value}`).join(' / ');
          const disabled = (v.qty || 0) <= 0;
          const delta = computeVariantDelta(variantModalProduct, v);
          return (
            <button
              key={v.id}
              className={`w-full text-left px-3 py-3 rounded-lg border bg-[#121212] transition ${
                disabled
                  ? 'border-gray-700 text-gray-500 cursor-not-allowed'
                  : 'border-gray-700 hover:border-purple-500 hover:bg-purple-900/20'
              }`}
              disabled={disabled}
              onClick={() => {
                addVariantToCart(variantModalProduct, v.id);
                setVariantModalOpen(false);
              }}
            >
              <div className="flex justify-between items-start gap-3">
                <div className="space-y-2 flex-1">
                  <div className="text-xs text-gray-400">Options</div>
                  <div className="flex flex-wrap gap-2">
                    {(v.options || []).map((o, idx) => (
                      <span
                        key={`${v.id}-${idx}`}
                        className="px-2 py-1 rounded bg-gray-800 border border-gray-700 text-sm text-gray-200"
                      >
                        {o.group}: {o.value}
                      </span>
                    ))}
                    {(!v.options || v.options.length === 0) && (
                      <span className="px-2 py-1 rounded bg-gray-800 border border-gray-700 text-sm text-gray-400">
                        No options
                      </span>
                    )}
                  </div>
                </div>
                <div className="text-right text-sm">
                  <div className="text-xs text-gray-400">
                    {delta >= 0 ? '+' : '-'} RM {Math.abs(delta).toFixed(2)}
                  </div>
                  <div className={`text-xs ${disabled ? 'text-red-400' : 'text-green-400'}`}>
                    {disabled ? 'Out of stock' : `Qty: ${v.qty ?? 0}`}
                  </div>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  </div>
)}
{bundleVariantModalOpen && bundleVariantContext.bundle && (
  <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-70 z-[12500] p-4">
    <div className="bg-[#1f1f1f] text-white w-full max-w-xl rounded-lg shadow-lg p-4 space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Choose bundle variants</h3>
        <button onClick={() => setBundleVariantModalOpen(false)} className="text-gray-300 hover:text-white">✕</button>
      </div>
      <div className="text-sm text-gray-300">
        {bundleVariantContext.bundle.name} (base price RM {(bundleVariantContext.bundle.price || 0).toFixed(2)})
      </div>
      <div className="space-y-3 max-h-96 overflow-y-auto">
        {(bundleVariantContext.bundle.items || [])
          .filter((bItem) => isAnyVariantItem(bItem))
          .map((bItem) => {
            const product = products.find((p) => p.id === bItem.id);
            const variants = product?.variants || [];
            const basePrice = Number(bItem.basePrice ?? product?.price ?? bItem.price ?? 0);
            const slots = Math.max(1, Number(bItem.quantity) || 1);
            const selectionValue = bundleVariantContext.selections[bItem.id];
            const selectionArray = Array.isArray(selectionValue)
              ? [...selectionValue]
              : Array(slots).fill(selectionValue || '');
            return (
              <div key={bItem.id} className="p-3 rounded border border-gray-700 bg-[#121212] space-y-1">
                <div className="text-sm font-semibold">{product?.name || bItem.name}</div>
                <div className="text-xs text-gray-400">
                  Qty in bundle: {bItem.quantity || 1} • Stock total: {getAvailableStock(bItem.id, ANY_VARIANT_ID)}
                </div>
                  <div className="space-y-3 mt-2">
                    {Array.from({ length: slots }).map((_, slotIdx) => {
                      const selectedId = selectionArray[slotIdx] || '';
                      return (
                        <div key={`${bItem.id}-slot-${slotIdx}`} className="space-y-2">
                          {slots > 1 && (
                            <div className="text-xs text-gray-400">
                              Selection {slotIdx + 1} of {slots}
                            </div>
                          )}
                          <div className="space-y-2">
                            {variants.map((v) => {
                              const delta = computeVariantDelta(product, v);
                              const label = getVariantLabel(v) || 'Variant';
                              const disabled = (v.qty || 0) <= 0;
                              const active = selectedId === v.id;
                              return (
                                <button
                                  key={v.id}
                                  type="button"
                                  disabled={disabled}
                                  className={`w-full text-left px-3 py-3 rounded-lg border transition ${
                                    disabled
                                      ? 'border-gray-700 text-gray-500 cursor-not-allowed'
                                      : active
                                        ? 'border-purple-500 bg-purple-900/30'
                                        : 'border-gray-700 hover:border-purple-500 hover:bg-purple-900/20'
                                  }`}
                                  onClick={() =>
                                    setBundleVariantContext((prev) => {
                                      const prevSel = prev.selections[bItem.id];
                                      const nextArr = Array.isArray(prevSel)
                                        ? [...prevSel]
                                        : Array(slots).fill(prevSel || '');
                                      nextArr[slotIdx] = v.id;
                                      return {
                                        ...prev,
                                        selections: { ...prev.selections, [bItem.id]: nextArr },
                                      };
                                    })
                                  }
                                >
                                  <div className="flex justify-between items-start gap-3">
                                    <div>
                                      <div className="font-medium">{label}</div>
                                      <div className="text-xs text-gray-400">Stock: {v.qty || 0}</div>
                                    </div>
                                    <div className="text-right text-sm">
                                      <div className="text-xs text-gray-400">
                                        {delta >= 0 ? '+' : '-'}RM {Math.abs(delta).toFixed(2)}
                                      </div>
                                    </div>
                                  </div>
                                </button>
                              );
                            })}
                            {variants.length === 0 && (
                              <div className="text-xs text-red-400">No variants available</div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
              </div>
            );
          })}
      </div>
      {(() => {
        const { deltaTotal } = buildBundleConfig(
          bundleVariantContext.bundle,
          bundleVariantContext.selections
        );
        return null;
      })()}
      <div className="flex justify-end gap-2">
        <button
          className="px-4 py-2 rounded bg-gray-700 hover:bg-gray-600"
          onClick={() => setBundleVariantModalOpen(false)}
        >
          Cancel
        </button>
        <button
          className="px-4 py-2 rounded bg-purple-600 hover:bg-purple-700"
          onClick={handleConfirmBundleVariants}
        >
          Add Bundle
        </button>
      </div>
    </div>
  </div>
)}
                      </div>

          <div className="flex-1 overflow-y-auto px-4 pb-6 scrollbar-thin scrollbar-thumb-purple-600 scrollbar-track-gray-900">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 xl:grid-cols-3 gap-4 py-4 text-base">
              {filteredProducts.map((item) => (
                bundles.some(b => b.id === item.id) ? (() => {
                  const availableSets = calculateBundleQuantity(item, products);
                  return (
                  // Bundle Card
                  <div
                    key={item.id}
                    className="bg-[#161616] p-4 rounded-xl cursor-pointer hover:ring-2 hover:ring-purple-600 transition duration-200 relative"
                  >
                    <div className="bg-purple-600 text-white text-center text-xs px-2 py-0.5 rounded-full">
                      Bundle
                    </div>
                    <h3 className="font-semibold text-xl mb-2 text-center">{item.name}</h3>
                    
                    {/* Bundle Items */}
                    <div className="mt-2 mb-3 space-y-1 max-h-24 overflow-y-auto text-sm text-gray-400">
                      {item.items?.map((bundleItem, idx) => (
                        <div key={`${item.id}-${idx}`} className="flex justify-between items-center">
                          <div className="truncate">
                            <span>{bundleItem.name}</span>
                            {bundleItem.variantName && (
                              <div className="text-[11px] text-gray-500">{bundleItem.variantName}</div>
                            )}
                          </div>
                          <span className="ml-2 whitespace-nowrap">x{bundleItem.quantity}</span>
                        </div>
                      )) || (
                        <div className="text-gray-500 text-center py-1">No items in bundle</div>
                      )}
                    </div>
                    
                    {/* Bundle Price and Quantity */}
                    <div className="mt-2 pt-2 border-t border-gray-700">
                      <div className="text-center">
                        <span className="text-gray-200 font-semibold text-lg">RM {item.price?.toFixed(2) || '0.00'}</span>
                      </div>
                      <div className="text-center">
                        <span className="text-base text-gray-500">
                          {availableSets > 0 ? `${availableSets} available` : 'Out of stock'}
                        </span>
                      </div>
                    </div>
                    
                    {cart.find(cartItem => cartItem.id === item.id)?.qty > (availableSets || 0) && (
                      <div className="text-red-500 text-xs mt-1">Exceeds available quantity</div>
                    )}
                    
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAddToCart(item);
                      }}
                      className="w-full mt-3 bg-purple-600 hover:bg-purple-700 text-white py-1.5 px-3 rounded-lg text-sm transition-colors"
                    >
                      Add to Cart
                    </button>
                  </div>
                );})() : (
                  // Regular Product Card
                  <div
                    key={item.id}
                    onClick={() => handleAddToCart(item)}
                    className="bg-[#161616] p-4 rounded-xl text-center cursor-pointer hover:ring-2 hover:ring-purple-600 transition duration-200"
                  >
                    <img
                      src={item.image || NoImage}
                      alt={item.name}
                      className="h-28 object-contain mb-2 rounded w-full"
                    />
                    <div className="font-semibold text-xl mb-1 truncate">{item.name}</div>
                    <div className="text-base text-gray-200 mb-1">RM {item.price?.toFixed(2) || '0.00'}</div>
                    <div className="text-sm text-gray-500">
                      {item.qty > 0 ? `${item.qty} available` : 'Out of stock'}
                    </div>
                    {cart.find(cartItem => cartItem.id === item.id)?.qty > (item.qty || 0) && (
                      <div className="text-red-500 text-sm">Exceeds available quantity</div>
                    )}
                  </div>
                )
              ))}
            </div>
          </div>
        </div>
        {/* Right Side: Order Panel */}
        <div className={`
          ${showOrder ? 'block' : 'hidden'} 
          lg:block
          w-full lg:w-2/5
          h-full overflow-y-auto
          bg-[#0e0e0e] lg:bg-transparent
          lg:static fixed inset-0 flex flex-col transition-transform duration-300 ease-in-out
          z-[9000] 
        `}>
          <div className="lg:hidden flex justify-end p-4">
              <button
                onClick={() => setShowOrder(false)}
                className="absolute top-4 right-4 z-[10001] text-white bg-transparent lg:hidden text-3xl font-bold p-1 rounded focus:outline-none"
                aria-label="Close Order Panel"
                style={{ lineHeight: 1 }}
              >
                &times;
              </button>
          </div>

          <div className="flex flex-col h-full p-4 border-[#161616] border-2 rounded-lg md:p-6 overflow-y-auto">
            <div className="text-2xl font-bold mb-4">Order {receiptNumber}</div>
            <div className="flex flex-wrap items-center justify-between gap-2 mb-4 w-full">
            

  <div className="flex flex-wrap gap-2">
{modeOptions.map((type) => (
      <button
        key={type}
        className={`px-3 py-1 border rounded text-lg ${
          mode === type
            ? 'bg-purple-600 text-white'
            : 'hover:bg-purple-600 hover:text-white text-gray-300'
        }`}
        onClick={() => setMode(type)}
      >
        {type}
      </button>
    ))}
  </div>
</div>

            <div className="flex justify-between items-center mb-2">
              <div className="text-sm text-gray-400">Item</div>
              {cart.length > 0 && (
                <button
                  onClick={handleClearAll}
                  className="text-xs bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded transition-colors"
                >
                  Clear All
                </button>
              )}
            </div>
            <hr className="mb-4 border-gray-700" />

            {cart.length === 0 ? (
  <div className="text-gray-500 text-sm">Cart is empty</div>
) : (
  <div className="space-y-3 max-h overflow-y-auto">
    {cart.map((item, idx) => {
      const isBundle = bundles.some(b => b.id === item.id);
      const quantity = item.qty ?? item.quantity ?? 0;

          return (
            <div
              key={item.id}
              className="flex justify-between items-center text-base"
            >
              <div>
                <div className="font-semibold text-lg">
                  {item.name}
                  {isBundle && <span className="ml-2 text-xs bg-purple-600 text-white px-2 py-0.5 rounded-full">Bundle</span>}
                </div>
                {(() => {
                  const productRef = products.find((p) => p.id === item.id);
                  const variantObj = productRef?.variants?.find(
                    (v) => (v.id || v.variantId) === item.variantId
                  );
                  const delta =
                    Number(item.variantPriceDelta || 0) ||
                    (productRef && variantObj ? computeVariantDelta(productRef, variantObj) : 0);
                  const label = item.variantName || variantObj?.options?.map((o) => `${o.group}: ${o.value}`).join(' / ');
                  if (!label) return null;
                  return (
                    <div className="text-sm text-gray-400">
                      {label}
                      {delta ? ` (+RM ${delta.toFixed(2)})` : ''}
                    </div>
                  );
                })()}
                
                {/* Show note if present */}
                {item.note && (
                  <div className="text-sm text-yellow-300 italic mt-1">
                    Note: {item.note}
              </div>
            )}
            
            {/* Show bundle items if it's a bundle */}
            {isBundle && item.items?.length > 0 && (
              <div className="mt-1 pl-2 text-xs text-gray-400 border-l-2 border-gray-600">
                {item.items.map((bundleItem, idx) => {
                  const productRef = products.find((p) => p.id === bundleItem.id);
                  const variantObj = productRef?.variants?.find(
                    (v) => (v.id || v.variantId) === bundleItem.variantId
                  );
                  const delta =
                    Number(bundleItem.variantPriceDelta || 0) ||
                    computeVariantDelta(productRef, variantObj);
                  const deltaText = delta ? ` (+RM ${delta.toFixed(2)})` : '';
                  return (
                    <div key={idx} className="flex justify-between">
                      <span>
                        • {bundleItem.name}
                        {bundleItem.variantName ? ` (${bundleItem.variantName})` : ''}
                        {deltaText}
                      </span>
                      <span className="ml-2">x{bundleItem.quantity * item.qty}</span>
                    </div>
                  );
                })}
              </div>
            )}
            
            <div className="flex items-center mt-1">
              <span className="text-sm text-gray-400 mr-2">Price:</span>
              <input
                type="number"
                value={item.price}
                step="0.01"
                min="0"
                onChange={(e) => handlePriceChange(item.id, parseFloat(e.target.value) || 0)}
                onClick={(e) => e.stopPropagation()}
                className="w-20 bg-gray-800 text-white text-sm px-2 py-1 rounded border border-gray-600 focus:border-purple-500 focus:ring-1 focus:ring-purple-500"
              />
              <span className="ml-2 text-sm text-gray-400">x {item.qty} = RM {(item.price * item.qty).toFixed(2)}</span>
            </div>
            
            {/* Note input field */}
            <div className="mt-1 w-full">
              <input
                type="text"
                placeholder="Add note..."
                value={itemNotes[item.id] || ''}
                onChange={(e) => handleNote(item.id, e.target.value)}
                onClick={(e) => e.stopPropagation()}
                className="w-full bg-gray-800 text-white text-sm px-2 py-1 rounded border border-gray-600 focus:border-purple-500 focus:ring-1 focus:ring-purple-500"
              />
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleQtyChange(item.id, -1, item.variantId || null, idx)}
              className="px-3 py-2 bg-[#7a4dc5] rounded hover:bg-[#573a85]"
            >
              -
            </button>
            <span className="text-lg">{quantity}</span>
            <button
              onClick={() => handleQtyChange(item.id, 1, item.variantId || null, idx)}
              disabled={
                quantity >= products.find((p) => p.id === item.id)?.qty
              }
              className="px-3 py-2 bg-[#7a4dc5] rounded hover:bg-[#573a85]"
            >
              +
            </button>
            <button
              onClick={() => handleRemoveItem(item.id, item.variantId || null, idx)}
              className="px-2 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-xs"
            >
              Remove
            </button>
          </div>
        </div>
      );
    })}
  </div>
)}

            {/* Footer section of order panel */}
            <div className="mt-auto p-4 border-t border-gray-800 bg-[#0e0e0e]">
              <div className="space-y-2 text-sm">
                <div className="mb-4">
                  <label className="block text-sm text-gray-400 mb-1">
                    Discount Code
                  </label>
                  <div className="flex gap-2">
                    <input
                      className="w-full bg-[#1f1f1f] text-white rounded px-3 py-2"
                      placeholder="Enter discount code"
                      value={discountCodeInput}
                      onChange={(e) => setDiscountCodeInput(e.target.value.toUpperCase())}
                    />
                    <button
                      className="px-3 py-2 bg-[#7a4dc5] rounded hover:bg-[#573a85]"
                      onClick={() => {
                        const code = discountCodeInput.trim().toUpperCase();
                        if (!code) {
                          setDiscountError('Enter a code');
                          return;
                        }
                        const match = discountOptions.find((d) => {
                          const codes = d.codes || (d.code ? [d.code] : []);
                          return codes.map((c) => c.toUpperCase()).includes(code);
                        });
                        if (!match) {
                          setDiscountError('Code not valid');
                          setSelectedDiscount(null);
                          setAppliedCode('');
                          return;
                        }
                        if (!isDiscountWithinValidity(match)) {
                          setDiscountError('Code expired');
                          setSelectedDiscount(null);
                          setAppliedCode('');
                          return;
                        }
                        const codes = match.codes || (match.code ? [match.code] : []);
                        const redeemedList = (match.redeemedCodes || []).map((c) => c.toUpperCase());
                        if (match.generated) {
                          if (!codes.map((c) => c.toUpperCase()).includes(code)) {
                            setDiscountError('Code not valid');
                            setSelectedDiscount(null);
                            setAppliedCode('');
                            return;
                          }
                          if (redeemedList.includes(code)) {
                            setDiscountError('Code already redeemed');
                            setSelectedDiscount(null);
                            setAppliedCode('');
                            return;
                          }
                        } else {
                          const totalAllowed = Number(match.voucherCount || codes.length || 0);
                          const redeemedCount = (match.redeemedCodes || []).length;
                          if (totalAllowed && redeemedCount >= totalAllowed) {
                            setDiscountError('Code fully redeemed');
                            setSelectedDiscount(null);
                            setAppliedCode('');
                            return;
                          }
                          if (codes.length > 0 && !codes.map((c) => c.toUpperCase()).includes(code)) {
                            setDiscountError('Code not valid');
                            setSelectedDiscount(null);
                            setAppliedCode('');
                            return;
                          }
                        }
                        setSelectedDiscount({ ...match, appliedCode: code });
                        setAppliedCode(code);
                        setDiscountError('');
                      }}
                    >
                      Apply
                    </button>
                    <button
                      className="px-3 py-2 bg-gray-700 rounded hover:bg-gray-600"
                      onClick={() => {
                        setSelectedDiscount(null);
                        setAppliedCode('');
                        setDiscountError('');
                        setDiscountCodeInput('');
                      }}
                    >
                      Clear
                    </button>
                  </div>
                  {discountError && <p className="text-red-400 text-xs mt-1">{discountError}</p>}
                  {appliedCode && selectedDiscount && (
                    <p className="text-green-400 text-xs mt-1">Applied {selectedDiscount.name} ({appliedCode})</p>
                  )}
                </div>
                <div className="flex justify-between">
                  <span>Sub total</span>
                  <span>RM {subtotal.toFixed(2)}</span>
                </div>
                {sst6Enabled && sst6Label && (
  <div className="flex justify-between">
    <span>{sst6Label}</span>
    <span>RM {sst6Amount.toFixed(2)}</span>
  </div>
)}
{sst8Enabled && sst8Label && (
  <div className="flex justify-between">
    <span>{sst8Label}</span>
    <span>RM {sst8Amount.toFixed(2)}</span>
  </div>
)}
{serviceTax10Enabled && serviceTax10Label && (
  <div className="flex justify-between">
    <span>{serviceTax10Label}</span>
    <span>RM {serviceTax10Amount.toFixed(2)}</span>
  </div>
)}
{selectedDiscount && (
                <div className="flex justify-between">
                  <span>Discount</span>
                  <span>-RM {(Number(discountAmount) || 0).toFixed(2)}</span>
                </div>
                )}
                <div className="flex justify-between font-semibold">
                  <span>Total</span>
                  <span>RM {total.toFixed(2)}</span>
                </div>
              </div>

              <button
                className={`mt-6 w-full py-2 rounded ${
                  !mode || cart.length === 0
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-purple-600 hover:bg-purple-700 text-white'
                }`}
                onClick={() => setShowPaymentModal(true)}
                disabled={!mode || cart.length === 0 }
              >
                Proceed to payment
              </button>
            </div>
          </div>
        </div>
      </div>

      {showPaymentModal && (
      <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center p-4 z-[14000]">
        <div className="relative bg-[#121212] w-full max-w-md p-6 rounded-lg text-white max-h-[90vh] overflow-y-auto z-[14001]">
          <button
            onClick={() => setShowPaymentModal(false)}
            className="absolute top-3 right-3 text-gray-300 hover:text-white text-3xl font-bold z-[10001]"
            aria-label="Close Payment Modal"
          >
            &times;
          </button>
          <h2 className="text-2xl font-bold mb-4">Payment</h2>
          <div className="mb-4">
      <input
        type="text"
        value={customerName}
        onChange={(e) => setCustomerName(e.target.value)}
        placeholder="Enter customer name"
        className="w-full px-3 py-2 rounded bg-[#2a2a2a] text-white focus:outline-none"
      />
    </div>

      {/* Totals */}
      <div className="mb-4 space-y-1">
        <div className="text-xl font-semibold text-gray-300">Total</div>
        <div className="text-3xl font-bold text-white">RM {total.toFixed(2)}</div>
        <div className="text-lg font-semibold text-gray-200">
          Balance: <span className="text-2xl text-white">RM {balance.toFixed(2)}</span>
        </div>
      </div>

      <div className="flex gap-2 mb-4 flex-wrap">
        {['Cash', 'Debit/Credit', 'E-wallet'].map((method) => (
          <button
            key={method}
            onClick={() => setPaymentMethod(method)}
            className={`px-4 py-2 rounded ${
              paymentMethod === method ? 'bg-purple-600' : 'bg-gray-700'
            }`}
          >
            {method}
          </button>
        ))}
      </div>

      {paymentMethod === 'Cash' && (
        <div className="mb-4">
          <p className="text-sm mb-2">Received Amount (Cash)</p>
          <div className="mb-2 w-full bg-[#1f1f1f] text-right px-3 py-2 rounded text-white text-xl tracking-widest">
            RM {cashInput || '0.00'}
          </div>

          {/* Keypad Grid */}
          <div className="grid grid-cols-3 gap-2 select-none">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, '.', 0, '⌫'].map((key) => (
              <button
                key={key}
                onClick={() => handleKeypadInput(key)}
                className="py-2 bg-gray-700 text-white rounded text-lg font-medium hover:bg-gray-600"
              >
                {key}
              </button>
            ))}
          </div>

        </div>
      )}

      {paymentMethod === 'Debit/Credit' && (
        <div className="mb-4">
          <p className="text-sm mb-2">Reference Number <span className="text-xs text-red-500">*</span></p>
          <input
            type="text"
            required
            value={cardRefNum}
            onChange={(e) => setCardRefNum(e.target.value)}
            placeholder="Enter reference number"
            className="w-full px-3 py-2 mt-2 rounded bg-[#2a2a2a] text-white focus:outline-none"
          />
        </div>
      )}
      <div className="flex justify-between mt-6 flex-wrap gap-2">
        <button
          onClick={() => setShowPaymentModal(false)}
          className="px-4 py-2 bg-gray-600 rounded flex-grow"
        >
          Cancel
        </button>

        <button
          onClick={handlePaymentConfirm}
          className="px-4 py-2 bg-purple-600 rounded flex-grow disabled:opacity-50"
          disabled={confirmLoading}
        >
          {confirmLoading ? (
            <svg
              className="animate-spin h-5 w-5 mx-auto text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
              />
            </svg>
          ) : (
            'Confirm'
          )}
        </button>
      </div>
    </div>
  </div>
)}

      {/* Receipt Modal */}
      {showReceipt && finalReceipt && (
  <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[15000] p-4">
  <div className="bg-[#121212] text-white w-full max-w-md p-6 rounded-lg shadow-lg text-center">
    <h2 className="text-2xl font-bold mb-4">Payment Successful</h2>
    <p className="text-sm mb-1">Receipt No: {finalReceipt.receiptNumber}</p>
    <p className="text-sm mb-6">
      {formatDate(finalReceipt.timestamp)} {formatTime(finalReceipt.timestamp)}
    </p>
      {/* Buttons Only */}
      <div className="flex justify-center gap-2 mt-4 flex-wrap max-w-md">
      <button
  onClick={() => {
    const domain = window.location.origin;
    const receiptId = finalReceipt.id;
    const publicUrl = `${domain}/publicReceipts/${receiptId}?pid=${publicId}`;
    setQrUrl(publicUrl);
    setQrModalOpen(true);

    try {
      const bc = new BroadcastChannel("pos_display");
      bc.postMessage({
        type: "SHOW_RECEIPT_QR",
        qrUrl: publicUrl,
      });
    } catch (err) {
      console.error("Failed to broadcast receipt QR:", err);
    }
  }}
  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded w-full"
>
  Generate QR
</button>
  <button
    onClick={handlePrint}
    className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded w-full"
  >
    Print
  </button>
  <button
    onClick={() => {
      setShowReceipt(false);
      loadProducts();
    }}
    className="text-white bg-purple-600 px-4 py-2 rounded hover:bg-purple-700 w-full"
  >
    Close
  </button>
</div>

      {/* Hidden Receipt for Printing */}
      <div
        ref={receiptRef}
        className="bg-white text-black w-full max-w-xs p-6 rounded-lg shadow-lg text-sm hidden print:block"
      >
        {companySnap.logoUrl && (
          <img
            src={companySnap.logoUrl}
            alt="Logo"
            className="h-16 mx-auto mb-4 object-contain"
          />
        )}
        <h3 className="text-center font-bold mb-1">{companySnap.companyName}</h3>
        {companySnap.regNumber && (companySnap.regType || '').toUpperCase() === 'BRN' && (
          <p className="text-center text-xs mb-1">
            {'Reg. Num: '}{companySnap.regNumber}
          </p>
        )}
        {companySnap.sstNumber && (
          <p className="text-center text-xs mb-1">
            {'SST Num: '}{companySnap.sstNumber}
          </p>
        )}
        <p className="text-center text-xs mb-2">
          {[companySnap.street, companySnap.postcode, companySnap.city, companySnap.state, companySnap.country]
            .filter(Boolean)
            .join(', ')}
        </p>
        <hr className="my-2 border-dashed border-gray-400" />
        {finalReceipt.queueNumber && (
        <p className="text-l font-bold">Queue No: {String(finalReceipt.queueNumber).padStart(3, '0')}</p>
      )}
      <p className="text-xs">Date: {formatDate(finalReceipt.timestamp)} {formatTime(finalReceipt.timestamp)}<br />Receipt No: {finalReceipt.receiptNumber}<br />Mode: {finalReceipt.mode}</p>
      <hr className="my-2 border-dashed border-gray-400" />
        <div className="flex justify-between font-bold">
          <span>Item</span>
          <span>Price</span>
        </div>
        {finalReceipt.items.map((item, idx) => (
          <div key={idx} className="mb-1">
            <div className="flex justify-between text-xs">
              <div>
                <span className="font-medium">{item.name} x{item.qty}</span>
                {(item.variantName || item.variantLabel) && (
                  <div className="text-[11px] text-gray-500">
                    {item.variantName || item.variantLabel}
                    {(() => {
                      const optionDelta = Array.isArray(item.selectedOptions)
                        ? item.selectedOptions.reduce((s, o) => s + Number(o.price || 0), 0)
                        : 0;
                      const deltaTotal = (Number(item.variantPriceDelta || 0) || optionDelta) * (item.qty || 1);
                      return deltaTotal !== 0 ? ` (+RM ${deltaTotal.toFixed(2)})` : '';
                    })()}
                  </div>
                )}
                {item.items && item.items.length > 0 && (
                  <div className="ml-3 mt-1 text-gray-600">
                    {item.items.map((bundleItem, bIdx) => {
                      const subQty = (bundleItem.quantity || 1) * (item.qty || 1);
                      const subOptDelta = Array.isArray(bundleItem.selectedOptions)
                        ? bundleItem.selectedOptions.reduce((s, o) => s + Number(o.price || 0), 0)
                        : 0;
                      const subDeltaTotal =
                        (Number(bundleItem.variantPriceDelta || 0) || subOptDelta) * subQty;
                      return (
                        <div key={bIdx} className="text-xs">
                          + {bundleItem.name} x{subQty}
                          {(bundleItem.variantName || bundleItem.variantLabel) && (
                            <div className="ml-4 text-[10px] text-gray-500">
                              {bundleItem.variantName || bundleItem.variantLabel}
                              {subDeltaTotal !== 0 ? ` (+RM ${subDeltaTotal.toFixed(2)})` : ''}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              {(() => {
                const qty = item.qty || 1;
                const optionDelta = Array.isArray(item.selectedOptions)
                  ? item.selectedOptions.reduce((sum, o) => sum + Number(o.price || 0), 0)
                  : 0;
                const deltaPerUnit = Number(item.variantPriceDelta || 0) || optionDelta;
                const basePerUnit = Number(item.price || 0) - deltaPerUnit;
                const baseTotal = basePerUnit * qty;
                const deltaTotal = deltaPerUnit * qty;
                return (
                  <div className="text-right">
                    <div>RM {baseTotal.toFixed(2)}</div>
                    {deltaTotal !== 0 && (
                      <div className="text-[11px] text-gray-500">+RM {deltaTotal.toFixed(2)}</div>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        ))}
        <hr className="my-2 border-dashed border-gray-400" />
        <div className="text-xs space-y-1">
          <div className="flex justify-between">
            <span>Subtotal</span>
            <span>RM {finalReceipt.subtotal.toFixed(2)}</span>
          </div>
          {taxEnabled && (
            <>
              {sst6Enabled && (
                <div className="flex justify-between">
                  <span>{sst6Label || 'SST (6%)'}</span>
                  <span>RM {finalReceipt.sst6Amount?.toFixed(2) ?? '0.00'}</span>
                </div>
              )}
              {sst8Enabled && (
                <div className="flex justify-between">
                  <span>{sst8Label || 'SST (8%)'}</span>
                  <span>RM {finalReceipt.sst8Amount?.toFixed(2) ?? '0.00'}</span>
                </div>
              )}
              {serviceTax10Enabled && (
                <div className="flex justify-between">
                  <span>Service Tax ({Number(serviceTax10Rate)}%)</span>
                  <span>RM {finalReceipt.serviceTax10Amount?.toFixed(2) ?? '0.00'}</span>
                </div>
              )}
            </>
          )}
          {finalReceipt.discount !== '-' && (
            <div className="flex justify-between">
              <span>Discount</span>
              <span>-RM {finalReceipt.discountAmount.toFixed(2)}</span>
            </div>
          )}
          {paymentMethod === 'Cash' && (
            <div className="flex justify-between">
              <span>Rounding</span>
              <span>
                {finalReceipt.rounding >= 0 ? '+' : ''}
                {Number(finalReceipt.rounding || 0).toFixed(2)}
              </span>
            </div>
          )}
          <div className="flex justify-between font-bold">
            <span>Total</span>
            <span>RM {finalReceipt.total.toFixed(2)}</span>
          </div>
          <div className="flex justify-between">
            <span>{paymentMethod}</span>
            <span>RM {finalReceipt.received.toFixed(2)}</span>
          </div>
          <div className="flex justify-between">
            <span>Change</span>
            <span>RM {finalReceipt.balance.toFixed(2)}</span>
          </div>
        </div>
        <hr className="my-2 border-dashed border-gray-400" />
        {finalReceipt.cardRefNum && (
        <>
        <p className="text-xs">Ref No: {finalReceipt.cardRefNum}</p>
        <hr className="my-2 border-dashed border-gray-400" />
        </>
      )}
        <p className="text-center text-xs mb-2">
          {companyInfo.thankYouNote || 'Thank you and come again!'}
        </p>
        {companyInfo.receiptFooter && (
          <div className="text-center text-xs text-gray-600 mt-2">
            {companyInfo.receiptFooter}
          </div>
        )}
        <br />
        <p className="text-center text-xs mb-2">Powered by CashMate</p>
      </div>
    </div>
  </div>
)}

{qrModalOpen && (
  <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[16000] p-4">
    <div className="bg-white rounded-lg p-6 max-w-sm w-full text-center text-black">
      <h2 className="text-xl font-semibold mb-4">Scan to View Receipt</h2>
      
      <div className="flex justify-center mb-4">
      <QRCode id="receiptQrCanvas" value={qrUrl} size={200} />
      </div>

      <p className="text-sm break-words mb-4">{qrUrl}</p>

      <div className="flex flex-col gap-2">
        <button
          className="bg-gray-800 text-white py-2 rounded"
          onClick={() => {
            navigator.clipboard.writeText(qrUrl);
            toast.success('URL copied to clipboard');
          }}
        >
          Copy URL
        </button>

        <button
  className="bg-red-500 text-white py-2 rounded"
  onClick={() => {
    setQrModalOpen(false);

    // 🧠 Tell Customer Display to go back to poster view
    try {
      const bc = new BroadcastChannel("pos_display");
      bc.postMessage({ type: "SHOW_POSTER" });
      setTimeout(() => bc.close(), 1000); // optional cleanup
    } catch (err) {
      console.error("Failed to broadcast poster view:", err);
    }
  }}
>
  Close
</button>
      </div>
    </div>
  </div>
)}
    </div>
  );
}
